import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  Button,
  Form,
  Input,
  ModalFooter,
} from "@telluscompany/design-system";

import { useLocalization } from "@i18n";

import {
  createAdminStructureReservoirManagementSchema,
  type AdminStructureReservoirManagementForm as ReservoirManagementFormValues,
} from "../../schemas";

export type AdminStructureReservoirManagementFormProps = {
  defaultValues: ReservoirManagementFormValues;
  isSubmitting: boolean;
  onSubmit: (values: ReservoirManagementFormValues) => Promise<void>;
  onCancel: () => void;
};

export const AdminStructureReservoirManagementForm = ({
  defaultValues,
  isSubmitting,
  onSubmit,
  onCancel,
}: AdminStructureReservoirManagementFormProps) => {
  const { translate } = useLocalization();

  const schema = useMemo(
    () => createAdminStructureReservoirManagementSchema(translate),
    [translate],
  );

  const form = useForm<ReservoirManagementFormValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    shouldFocusError: true,
    defaultValues,
  });

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit, () => {
          void form.trigger(undefined, { shouldFocus: true });
        })}
      >
        <div className="flex flex-col gap-4 px-8 py-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="occupiedArea"
              label={translate(
                "admin.structures.reservoirManagement.label.occupiedArea",
              )}
              type="text"
              inputMode="decimal"
            />
            <Input
              control={form.control}
              name="totalVolume"
              label={translate(
                "admin.structures.reservoirManagement.label.totalVolume",
              )}
              type="text"
              inputMode="decimal"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="totalOccupiedVolume"
              label={translate(
                "admin.structures.reservoirManagement.label.totalOccupiedVolume",
              )}
              type="text"
              inputMode="decimal"
            />
            <Input
              control={form.control}
              name="availableVolume"
              label={translate(
                "admin.structures.reservoirManagement.label.availableVolume",
              )}
              type="text"
              inputMode="decimal"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="rejectOccupiedVolume"
              label={translate(
                "admin.structures.reservoirManagement.label.rejectOccupiedVolume",
              )}
              type="text"
              inputMode="decimal"
            />
            <Input
              control={form.control}
              name="waterOccupiedVolume"
              label={translate(
                "admin.structures.reservoirManagement.label.waterOccupiedVolume",
              )}
              type="text"
              inputMode="decimal"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              control={form.control}
              name="waterLevel"
              label={translate(
                "admin.structures.reservoirManagement.label.waterLevel",
              )}
              type="text"
              inputMode="decimal"
            />
            <Input
              control={form.control}
              name="recordedOn"
              label={translate(
                "admin.structures.reservoirManagement.label.recordedOn",
              )}
              type={"date" as "text"}
            />
          </div>
        </div>
        <ModalFooter
          footer={
            <Button type="submit" disabled={isSubmitting}>
              {translate("admin.structures.modal.button.save")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("admin.structures.modal.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
